import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, bootPage, withFresh, ApiError } from '../api';
import { abbrM, abbrPct, money, nfmt, pctF, signedPct } from '../format';
import { lbl } from '../labels';
import { Card, Cards } from '../components/Card';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { InfoTip } from '../components/InfoTip';
import { Modal } from '../components/Modal';
import { Select } from '../components/Select';
import { Menu, useMenu } from '../components/Menu';
import { Table } from '../components/Table';
import { Tabs } from '../components/Tabs';
import { ValueChart } from './ValueChart';

/**
 * Home — the first ported page (P3), and the proof of the pipeline: the payload is `/api/v1/home`, which
 * is `api/pages.js`'s `homePage()` — the same object views/dashboard.ejs renders from. Every figure below
 * is formatted by web/src/format.ts, which copies app.locals' formulas rather than inventing rounding.
 *
 * Not ported yet, and visible as such: the total-value chart (needs Chart.js in the SPA), the list row
 * actions (rename/delete/reorder — they are writes and belong with the list page), and the "…" menu.
 */
type Summary = {
  totalValue: number;
  totalCost: number;
  totalUnrealized: number;
  dayChange: number;
  dayChangePct: number | null;
  dayAsOf?: string;
  daySessionDate?: string;
  totalGain: number;
  /** Set when the newest price session is today, which is what the page's own ⓘ reads. */
  pricesLiveSession?: boolean;
  totalRealized?: number;
  totalDividends?: number;
};

type Holding = {
  ticker: string;
  closed?: boolean;
  exposurePct?: number | null;
  shares: number;
  avgCost?: number;
  cost: number;
  value: number;
  price: number;
  unrealized: number;
  unrealizedPct: number | null;
  dayGain?: number;
  dayGainPct?: number | null;
  realized?: number;
  realizedPct?: number | null;
  unpriced?: boolean;
};

type ListRow = {
  id: number;
  name: string;
  type: string;
  value: number;
  dayChange?: number;
  dayChangePct?: number | null;
  symbols: number;
  costBasis?: number;
  unrealized?: number;
  unrealizedPct?: number | null;
  realized?: number;
  realizedPct?: number | null;
};

/** What the server did about price freshness on this load (openspec: market-data). */
type PriceInfo = {
  fetchedAt: string | null;
  stale: boolean;
  staleCount: number;
  marketOpen: boolean;
  /** Present only when a refresh ran; `forced` when `?fresh=1` asked for one. */
  refreshMs?: number;
  refreshed?: number;
  timedOut?: boolean;
  /** Why nothing landed, when nothing did: backoff | provider | error | empty. */
  skipped?: string;
  error?: string;
  forced?: boolean;
};

/** The one-line outcome of a refresh, for the tip that already reports freshness. */
function refreshOutcome(p?: PriceInfo): string {
  if (!p) return '';
  if (p.refreshed) return `${p.refreshMs ?? 0}ms · ${p.refreshed} ticker${p.refreshed === 1 ? '' : 's'}`;
  if (p.skipped === 'backoff') return 'pipeline backoff — no request made';
  if (p.skipped === 'provider') return 'provider refused — cached';
  if (p.skipped === 'error') return `failed${p.error ? ': ' + p.error : ''}`;
  if (p.timedOut) return `timed out at ${p.refreshMs ?? 0}ms`;
  return 'nothing to refresh';
}

type HomePayload = { summary: Summary | null; rows: Holding[]; lists: ListRow[]; allocation: unknown[]; prices?: PriceInfo };

export function Home() {
  const [tab, setTab] = useState('lists');
  // Leaving My Lists closes the … menu, as views/dashboard.ejs does on the tab event.
  useEffect(() => {
    if (tab !== 'lists') listsMenu.setOpen(false);
  }, [tab]);
  // The lists' "…" menu, reorder mode and the new-list form. They live in the tab bar's trailing slot on the
  // EJS page (partials/home-tabs-end.ejs), with the same ids, because the reorder code and the browser suite
  // address them by id.
  const listsMenu = useMenu({ menu: 'lists-menu', trigger: 'lists-menu-btn' });
  const [reordering, setReordering] = useState(false);
  const [order, setOrder] = useState<number[] | null>(null);
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  // Declared with the other hooks, ABOVE the loading/error returns: a hook below them renders only once the data
  // has arrived, so the second render has one hook more than the first — React #310, "rendered more hooks than
  // during the previous render", which is what the page showed after a reorder when it came back from the list.
  const [newListType, setNewListType] = useState('general');
  const qc = useQueryClient();
  const q = useQuery<HomePayload, ApiError>({
    queryKey: ['home'],
    // Served with the shell: the first paint has the portfolio instead of a spinner.
    initialData: bootPage<HomePayload>('home'),
    queryFn: () => apiGet<HomePayload>(withFresh('/api/v1/home')) });

  if (q.isLoading) return <main>Loading…</main>;
  if (q.error) return <main className="panel-card">Could not load your portfolio: {q.error.message}</main>;

  const { rows, lists: payloadLists } = q.data!;
  // A brand-new account has no summary: the EJS dashboard still renders its cards, tabs and menu, and hides
  // only the chart. Returning an "empty" page here instead was why the menu was missing for a new user.
  const summary: Summary = q.data!.summary ?? {
    totalValue: 0,
    totalCost: 0,
    totalUnrealized: 0,
    dayChange: 0,
    dayChangePct: null,
    totalGain: 0,
  };
  // Reorder mode draws the order the reader moved into; outside it the payload's order is the truth.
  const lists = order
    ? order.map((id) => payloadLists.find((l) => l.id === id)).filter((l): l is ListRow => !!l)
    : payloadLists;

  const move = (id: number, dir: -1 | 1) => {
    const ids = lists.map((l) => l.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setOrder(ids);
  };

  const persistOrder = async () => {
    const ids = lists.map((l) => l.id);
    await fetch('/api/v1/lists/reorder', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: ids.join(',') }),
    });
    setReordering(false);
    setOrder(null);
    qc.invalidateQueries({ queryKey: ['home'] });
  };

  const createList = async () => {
    const name = newListName.trim();
    if (!name) return;
    await fetch('/api/v1/lists', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, type: newListType }),
    });
    setNewListName('');
    setNewListOpen(false);
    listsMenu.setOpen(false);
    // A real navigation, like the EJS form's POST: it also means "created" and "on screen" are the same moment.
    window.location.assign('/');
  };


  const posNeg = (n: number) => (n >= 0 ? 'pos' : 'neg');

  return (
    <main>
      <h1>Summary</h1>
      <Cards>
        <Card label={lbl("Total value")} value={<span title={money(summary.totalValue)}>{abbrM(summary.totalValue)}</span>} />
        <Card label={lbl("Cost basis")} value={<span title={money(summary.totalCost)}>{abbrM(summary.totalCost)}</span>} />
        <Card
          label={lbl("Unrealized")}
          valueClass={posNeg(summary.totalUnrealized)}
          value={abbrM(summary.totalUnrealized) + (summary.totalCost > 0 ? signedPct((summary.totalUnrealized / summary.totalCost) * 100) : '')}
        />
        <Card
          label={lbl('Day change')}
          // The EJS tip is the DATA's date, not a sentence about it: "As of 2026-10-30". The date is what a
          // reader needs to judge the number, and inventing copy here changed what the page said.
          labelTip={
            summary.daySessionDate || summary.dayAsOf ? (
              <InfoTip anchor="card" place="below-left" nowrap letterSpacing={0.5}>
                {summary.pricesLiveSession
                  ? 'Live pricing'
                  : `As of ${summary.daySessionDate || String(summary.dayAsOf || '').slice(0, 10)}`}
                {/* ?fresh=1 reports itself: a forced refresh that cannot be seen is indistinguishable from
                    one that never ran. Same tip, so no new markup on the page. */}
                {q.data?.prices?.forced ? ` · forced ${refreshOutcome(q.data.prices)}` : ''}
              </InfoTip>
            ) : undefined
          }
          valueClass={posNeg(summary.dayChange)}
          value={abbrM(summary.dayChange) + (summary.dayChangePct != null ? signedPct(summary.dayChangePct) : '')}
        />
        <Card
          label={lbl("Total gain")}
          // views/partials/tips/total-gain.ejs: three figures, one per line, not a sentence about them.
          tip={
            <InfoTip anchor="card" place="below-left" nowrap>
              {' '}Unrealized: {abbrM(summary.totalUnrealized)}
              {summary.totalCost > 0 ? ` (${((summary.totalUnrealized / summary.totalCost) * 100).toFixed(2)}%)` : ''}
              <br />
              Realized: {money(summary.totalRealized ?? 0)}
              <br />
              Dividends: {money(summary.totalDividends ?? 0)}
            </InfoTip>
          }
          valueClass={posNeg(summary.totalGain)}
          value={<span title={money(summary.totalGain)}>{abbrM(summary.totalGain)}</span>}
        />
      </Cards>

      {/* EJS puts the chart ABOVE the tabs; the A/B pass had flagged the SPA for not having it at all. */}
      <ValueChart />

      <section className="panel-card" style={{ marginTop: 0 }}>
        <Tabs
          id="home-tabs"
          variant="segmented"
          raised
          ariaLabel="Home view"
          active={tab}
          // The EJS tab bar carries its own margins (`margin: 4px 0 12px`); without them the table starts
          // 16px higher here and every row below it is vertically out of step with the other front end.
          style={{ margin: '4px 0 12px' }}
          onChange={setTab}
          items={[
            { key: 'lists', label: 'My Lists', pane: 'home-tab-lists' },
            { key: 'holdings', label: 'My Holdings', pane: 'home-tab-holdings' },
          ]}
          trailing={
            <span style={{ position: 'relative', marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
              {reordering ? (
                <>
                  <button
                    type="button"
                    id="reorder-cancel"
                    className="ghost btn-confirm-x"
                    title="Cancel reordering"
                    onClick={() => {
                      setReordering(false);
                      setOrder(null);
                    }}
                  >
                    ✕
                  </button>
                  <button type="button" id="reorder-done" className="ghost btn-confirm-ok" title="Done reordering" onClick={persistOrder}>
                    ✓
                  </button>
                </>
              ) : null}
              {/* The … menu belongs to My Lists: views/dashboard.ejs hides it on the holdings tab
                  (`#lists-menu-btn.style.display = onLists ? 'inline-flex' : 'none'`) and closes it, so an open
                  menu cannot survive the switch. */}
              <button
                {...listsMenu.triggerProps}
                type="button"
                ref={listsMenu.triggerRef}
                style={{ display: tab === 'lists' ? 'inline-flex' : 'none' }}
                className="ghost"
                aria-label="List actions"
              >
                …
              </button>
              <Menu {...listsMenu.menuProps} align="right" minWidth={180}>
                {/* EJS's two `menu-row` buttons (14px, 8px 14px, 35px). These had been bordered ghost
                    buttons inside a 6px-padded wrapper: 37px rows, 16px type, a box around each row. */}
                <Button
                  shape="menu-row"
                  className="t-dense"
                  attrs={{ role: 'menuitem', 'data-menu-item': '' }}
                  id="new-list-open"
                  icon={
                    <span className="menu-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  }
                  // views/dashboard.ejs's handler is `hideListsMenu(); PFModal.open(...)` — the menu closes before the
                  // dialog opens, so the two never stack.
                  onClick={() => {
                    listsMenu.setOpen(false);
                    setNewListOpen(true);
                  }}
                >
                  New list
                </Button>
                <Button
                  shape="menu-row"
                  className="t-dense"
                  attrs={{ role: 'menuitem', 'data-menu-item': '' }}
                  id="reorder-toggle"
                  icon={
                    <span className="menu-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M8 4v16M8 20l-3-3M8 20l3-3" />
                        <path d="M16 20V4M16 4l-3 3M16 4l3 3" />
                      </svg>
                    </span>
                  }
                  onClick={() => {
                    setReordering(true);
                    listsMenu.setOpen(false);
                  }}
                >
                  Reorder
                </Button>
              </Menu>
            </span>
          }
        />

        {/* views/dashboard.ejs opens the new-list form as a DIALOG (`#new-list-modal`, size sm) from the … menu.
            The SPA had an inline strip under the tab bar instead: same fields, different thing — no backdrop, no
            focus return, no Escape, and it pushed the table down while open. */}
        <Modal id="new-list-modal" open={newListOpen} onClose={() => setNewListOpen(false)} title="New list" size="sm">
          <form
            method="post"
            action="/lists"
            id="new-list-form"
            onSubmit={(e) => {
              e.preventDefault();
              void createList();
            }}
          >
            <Field label="Name" htmlFor="name" required>
              <input
                id="name"
                name="name"
                required
                placeholder="Long-term holds"
                data-modal-autofocus
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
              />
            </Field>
            <Field label="Type" htmlFor="type">
              <div className="field-row">
                {/* A native <select> here was 34px and unstyled: views/dashboard.ejs's Type field is the app's
                    own `.sel` control (35px, its own chevron), which is what the Select component renders. */}
                <Select
                  id="type"
                  name="type"
                  value={newListType}
                  onChange={(v) => setNewListType(v)}
                  style={{ flex: 1, minWidth: 0 }}
                  options={[
                    { value: 'general', label: 'General' },
                    { value: 'watchlist', label: 'Watchlist' },
                    { value: 'tax_deferred', label: 'Tax-deferred' },
                  ]}
                />
                <span
                  className="t-small"
                  id="type-hint"
                  style={{
                    display: newListType === 'watchlist' ? undefined : 'none',
                    color: 'var(--muted)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Excluded from totals
                </span>
              </div>
            </Field>
            <div className="modal-actions">
              <button className="ghost" type="button" id="new-list-cancel" data-modal-close onClick={() => setNewListOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="ghost btn-accent-text" id="new-list-submit">
                Create list
              </button>
            </div>
          </form>
        </Modal>

        {tab === 'lists' ? (
          <div id="home-tab-lists" role="tabpanel">
            {/* views/dashboard.ejs's own line, always in the DOM and shown by reorder mode. */}
            <div
              id="reorder-hint"
              className="t-body"
              style={{
                display: reordering ? 'block' : 'none',
                margin: '8px 0',
                color: 'var(--muted)',
              }}
            >
              Drag rows by the ⋮⋮ handle to reorder, then choose … → Done.
            </div>
            <Table
              tableId="lists-table"
              tableClass={reordering ? 'reorder-on' : 'reorder-off'}
              // The EJS columns, in the EJS order: Name, Symbols, Type, Cost Basis, Market Value, Day
              // Change, Unrealized, Realized. The first SPA version had five columns of its own and printed
              // "$0.00" for a watchlist, where the EJS page prints "—" — a watchlist has no cost basis, and
              // zero is a different claim.
              expandable={false}
              columns={[
                { label: lbl('Name') },
                { label: lbl('Symbols'), num: true },
                { label: lbl('Type') },
                { label: lbl('Cost Basis'), num: true },
                { label: lbl('Market Value'), num: true },
                { label: lbl('Day Change'), num: true },
                { label: lbl('Unrealized'), num: true },
                { label: lbl('Realized'), num: true },
              ]}
              // Drag-to-reorder is the Table's own (`reorder`): it marks the row draggable, records the dragged
              // index on dragstart and calls onDrop(from, to). Hand-rolling drag handlers here duplicated it and
              // fought it — the row attributes I set were never applied, so only the name link dragged.
              reorder={{
                enabled: reordering,
                // The drag preview is the TABLE's (it moves the row on every dragover, as the EJS table does), so a
                // page only says what a finished drag MEANS: here, the new sequence, committed by Done.
                onDrop: (from, to, order) => {
                  // Adopt the table's previewed sequence verbatim when it hands one over, so the committed order
                  // is exactly the order the reader saw while dragging.
                  if (order) {
                    setOrder(order.map(Number));
                    return;
                  }
                  const ids = lists.map((l) => l.id);
                  if (from < 0 || to < 0 || from >= ids.length || to >= ids.length) return;
                  const [moved] = ids.splice(from, 1);
                  ids.splice(to, 0, moved);
                  setOrder(ids);
                },
              }}
              rows={lists.map((l) => ({
                key: l.id,
                // The row hook the EJS table carries and the stylesheet/reorder code addresses.
                // `draggable` is what views/dashboard.ejs's setReorderIcons() toggles per row: a row cannot be
                // picked up outside reorder mode.
                rowAttrs: { 'data-list-id': l.id, draggable: reordering },
                cells: [
                  {
                    className: 'reorder-cell',
                    content: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                        {/* views/dashboard.ejs renders the drag handle ALWAYS and keeps the arrow buttons in the
                            DOM (hidden by `.reorder-arrows` until reorder mode) — the SPA showed neither
                            outside reorder mode, so a row was just a name and the ⋮⋮ handle was missing. */}
                        {/* `#lists-table.reorder-off .drag-handle { display: none }` is in app.css, so the
                            handle and the arrows sit in the DOM at all times and only PAINT in reorder mode —
                            the EJS table's own arrangement. */}
                        <span
                          className="drag-handle"
                          title="Drag to reorder"
                          style={{ cursor: reordering ? 'grab' : 'default', color: 'var(--muted)', userSelect: 'none' }}
                        >
                          ⋮⋮
                        </span>
                        <span
                          className="reorder-arrows"
                          style={{ display: 'none', flexDirection: 'column', gap: 0, lineHeight: 1 }}
                        >
                          <button
                            type="button"
                            className="arrow-btn t-micro btn-quiet btn-xs"
                            data-dir="up"
                            data-list={l.id}
                            title="Move up"
                            aria-label="Move up"
                            style={{ display: 'inline-flex' }}
                            onClick={() => move(l.id, -1)}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="arrow-btn t-micro btn-quiet btn-xs"
                            data-dir="down"
                            data-list={l.id}
                            title="Move down"
                            aria-label="Move down"
                            style={{ display: 'inline-flex' }}
                            onClick={() => move(l.id, 1)}
                          >
                            ▼
                          </button>
                        </span>
                        <a href={`/lists/${l.id}`} className="list-name-link">
                          {l.name}
                        </a>
                      </div>
                    ),
                  },
                  nfmt(l.symbols),
                  <span key="t" className="tag">{l.type.replace('_', ' ')}</span>,
                  l.costBasis != null ? <span key="c" title={money(l.costBasis)}>{abbrM(l.costBasis)}</span> : '—',
                  l.value != null ? <span key="v" title={money(l.value)}>{abbrM(l.value)}</span> : '—',
                  l.dayChange != null && (l.value ?? 0) > 0 ? (
                    <span key="d" className={l.dayChange >= 0 ? 'pos' : 'neg'}>
                      <span title={money(l.dayChange)}>{abbrM(l.dayChange)}</span>
                      {l.dayChangePct != null ? ` (${pctF(l.dayChangePct)}%)` : ''}
                    </span>
                  ) : (
                    '—'
                  ),
                  (l.costBasis ?? 0) > 0 && l.unrealized != null ? (
                    <span key="u" className={l.unrealized >= 0 ? 'pos' : 'neg'}>
                      <span title={money(l.unrealized)}>{abbrM(l.unrealized)}</span>
                      {l.unrealizedPct != null ? ` (${pctF(l.unrealizedPct)}%)` : ''}
                    </span>
                  ) : (
                    '—'
                  ),
                  // Realized writes its percentage with abbrPct (`(151%)`), not pctF's `(151)` — the EJS
                  // table's realized column has the thousands-separated form, and the two columns are read
                  // side by side.
                  l.realized ? (
                    <span key="r" className={l.realized >= 0 ? 'pos' : 'neg'}>
                      <span title={money(l.realized) + (l.realizedPct != null ? ` (${abbrPct(l.realizedPct)})` : '')}>
                        {abbrM(l.realized)}
                      </span>
                      {l.realizedPct != null ? ` (${abbrPct(l.realizedPct)})` : ''}
                    </span>
                  ) : (
                    '—'
                  ),
                ],
              }))}
              empty="No lists yet."
            />
          </div>
        ) : (
          <div id="home-tab-holdings" role="tabpanel">
            {/* The EJS table's ten columns, in the EJS order. The first SPA version rendered six (Ticker,
                Shares, Price, Value, Unrealized, Day) and lost the ticker's `closed` marker with them: a
                closed position read as a live holding whose shares and value happened to be missing. */}
            <Table
              tableId="holdings-overview-table"
              tableClass=""
              expandable={false}
              columns={[
                { label: lbl('Ticker') },
                { label: lbl('Exposure'), num: true },
                { label: lbl('Shares'), num: true },
                { label: lbl('AC/share'), num: true },
                { label: lbl('Cost Basis'), num: true },
                { label: lbl('Price'), num: true },
                { label: lbl('Market Value'), num: true },
                { label: lbl('Day Change'), num: true },
                { label: lbl('Unrealized'), num: true },
                { label: lbl('Realized'), num: true },
              ]}
              rows={rows.map((h) => ({
                key: h.ticker,
                cells: [
                  <>
                    <strong>{h.ticker}</strong>
                    {h.closed ? (
                      <>
                        {' '}
                        <span className="t-tiny" style={{ color: 'var(--muted)' }}>
                          closed
                        </span>
                      </>
                    ) : null}
                  </>,
                  h.exposurePct != null ? `${pctF(h.exposurePct)}%` : '—',
                  h.closed ? '—' : nfmt(h.shares),
                  h.avgCost ? abbrM(h.avgCost) : '—',
                  h.closed ? '—' : <span title={money(h.cost)}>{abbrM(h.cost)}</span>,
                  <span title={h.price != null ? money(h.price) : ''}>{h.price != null ? abbrM(h.price) : '—'}</span>,
                  h.closed ? '—' : <span title={money(h.value)}>{abbrM(h.value)}</span>,
                  h.dayGain != null ? (
                    <span
                      className={h.dayGain >= 0 ? 'pos' : 'neg'}
                      title={
                        money(h.dayGain) + (h.dayGainPct != null ? ` (${h.dayGainPct.toFixed(2)}%)` : '')
                      }
                    >
                      {abbrM(h.dayGain)}
                      {h.dayGainPct != null ? ` (${pctF(h.dayGainPct)}%)` : ''}
                    </span>
                  ) : (
                    '—'
                  ),
                  h.cost > 0 && h.unrealized != null ? (
                    <span
                      className={h.unrealized >= 0 ? 'pos' : 'neg'}
                      title={
                        money(h.unrealized) + (h.unrealizedPct != null ? ` (${h.unrealizedPct.toFixed(2)}%)` : '')
                      }
                    >
                      {abbrM(h.unrealized)}
                      {h.unrealizedPct != null ? ` (${pctF(h.unrealizedPct)}%)` : ''}
                    </span>
                  ) : (
                    '—'
                  ),
                  h.realized ? (
                    <span
                      className={h.realized >= 0 ? 'pos' : 'neg'}
                      title={money(h.realized) + (h.realizedPct != null ? ` (${abbrPct(h.realizedPct)})` : '')}
                    >
                      {abbrM(h.realized)}
                      {h.realizedPct != null ? ` (${abbrPct(h.realizedPct)})` : ''}
                    </span>
                  ) : (
                    '—'
                  ),
                ],
              }))}
              empty="No holdings yet."
            />
          </div>
        )}
      </section>
    </main>
  );
}
